import React, { useEffect, useState } from 'react';
import { Brain, Loader2, AlertTriangle, CheckCircle, Copy, Star, BarChart3 } from 'lucide-react';
import { adminDb } from '../lib/adminDb';

interface QuestionQuality {
  id: string;
  question: string;
  chapter_id: number;
  difficulty: string;
  qualityScore: number;
  errorRate: number;
  answerCount: number;
  issues: string[];
}

interface DuplicatePair {
  q1: { id: string; question: string; chapter: number };
  q2: { id: string; question: string; chapter: number };
  similarity: number;
}

export const ContentIntelligencePage: React.FC = () => {
  const [questions, setQuestions] = useState<QuestionQuality[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicatePair[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'quality' | 'duplicates'>('quality');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [questionsRes, usersRes] = await Promise.all([
      adminDb.from('questions').select('*'),
      adminDb.from('users').select('data'),
    ]);

    const qs = questionsRes.data || [];
    const users = usersRes.data || [];

    // Calculate error rates from user data
    const errorCounts: Record<string, number> = {};
    const answerCounts: Record<string, number> = {};
    users.forEach(u => {
      const completed = u.data?.completedQuestions || [];
      const wrong = u.data?.wrongAnswers || [];
      if (Array.isArray(completed)) completed.forEach((id: string) => { answerCounts[id] = (answerCounts[id] || 0) + 1; });
      if (Array.isArray(wrong)) wrong.forEach((id: string) => { errorCounts[id] = (errorCounts[id] || 0) + 1; });
    });

    // Quality scoring
    const scored: QuestionQuality[] = qs.map(q => {
      const answers = answerCounts[q.id] || 0;
      const errors = errorCounts[q.id] || 0;
      const errorRate = answers > 0 ? Math.round((errors / answers) * 100) : 0;
      const issues: string[] = [];

      let qualityScore = 100;

      // Penalty for too high error rate
      if (errorRate > 80) { qualityScore -= 30; issues.push('Extrem hohe Fehlerrate'); }
      else if (errorRate > 60) { qualityScore -= 15; issues.push('Hohe Fehlerrate'); }

      // Penalty for too easy (could be trivial)
      if (errorRate < 5 && answers > 5) { qualityScore -= 10; issues.push('Moeglicherweise zu einfach'); }

      // Penalty for missing fields
      if (!q.question || q.question.length < 20) { qualityScore -= 20; issues.push('Frage zu kurz'); }
      if (!q.explanation) { qualityScore -= 10; issues.push('Keine Erklaerung'); }
      if (!q.image_url && !q.visual_prompt) { qualityScore -= 5; issues.push('Kein Bild/Prompt'); }

      // Penalty for no answers yet
      if (answers === 0) { qualityScore -= 5; issues.push('Noch nicht beantwortet'); }

      return {
        id: q.id, question: q.question || '', chapter_id: q.chapter_id,
        difficulty: q.difficulty || 'mittel',
        qualityScore: Math.max(0, qualityScore), errorRate, answerCount: answers, issues,
      };
    }).sort((a, b) => a.qualityScore - b.qualityScore);

    // Duplicate detection (simple substring similarity)
    const dups: DuplicatePair[] = [];
    for (let i = 0; i < qs.length && dups.length < 20; i++) {
      for (let j = i + 1; j < qs.length && dups.length < 20; j++) {
        const text1 = (qs[i].question || '').toLowerCase().trim();
        const text2 = (qs[j].question || '').toLowerCase().trim();
        if (text1.length < 10 || text2.length < 10) continue;
        // Simple similarity: Jaccard on words
        const words1 = new Set(text1.split(/\s+/));
        const words2 = new Set(text2.split(/\s+/));
        const intersection = [...words1].filter(w => words2.has(w)).length;
        const union = new Set([...words1, ...words2]).size;
        const sim = union > 0 ? Math.round((intersection / union) * 100) : 0;
        if (sim >= 70) {
          dups.push({
            q1: { id: qs[i].id, question: qs[i].question?.substring(0, 80) || '', chapter: qs[i].chapter_id },
            q2: { id: qs[j].id, question: qs[j].question?.substring(0, 80) || '', chapter: qs[j].chapter_id },
            similarity: sim,
          });
        }
      }
    }
    dups.sort((a, b) => b.similarity - a.similarity);

    setQuestions(scored);
    setDuplicates(dups);
    setLoading(false);
  };

  const avgQuality = questions.length > 0 ? Math.round(questions.reduce((s, q) => s + q.qualityScore, 0) / questions.length) : 0;
  const lowQuality = questions.filter(q => q.qualityScore < 60).length;
  const noExplanation = questions.filter(q => q.issues.includes('Keine Erklaerung')).length;

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><Brain size={24} /> Content-Intelligence</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header"><h1><Brain size={24} /> Content-Intelligence</h1></div>

      <div className="grid-cols-4 mb-6">
        <div className="kpi-card"><span className="kpi-label">Ø Qualitaet</span><span className="kpi-value" style={{ color: avgQuality >= 70 ? 'var(--success)' : 'var(--warning)' }}>{avgQuality}%</span></div>
        <div className="kpi-card"><span className="kpi-label">Schwache Fragen</span><span className="kpi-value" style={{ color: 'var(--danger)' }}>{lowQuality}</span></div>
        <div className="kpi-card"><span className="kpi-label">Ohne Erklaerung</span><span className="kpi-value" style={{ color: 'var(--warning)' }}>{noExplanation}</span></div>
        <div className="kpi-card"><span className="kpi-label">Duplikat-Verdacht</span><span className="kpi-value" style={{ color: duplicates.length > 0 ? 'var(--danger)' : 'var(--success)' }}>{duplicates.length}</span></div>
      </div>

      <div className="flex gap-2 mb-6">
        <button className={`btn ${tab === 'quality' ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setTab('quality')}>
          <Star size={14} /> Qualitaets-Scores ({questions.length})
        </button>
        <button className={`btn ${tab === 'duplicates' ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setTab('duplicates')}>
          <Copy size={14} /> Duplikate ({duplicates.length})
        </button>
      </div>

      {tab === 'quality' && (
        <div className="table-container">
          <table>
            <thead><tr><th>Score</th><th>Frage</th><th>Kap.</th><th>Fehlerrate</th><th>Probleme</th></tr></thead>
            <tbody>
              {questions.slice(0, 50).map(q => (
                <tr key={q.id}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div style={{ width: 40, height: 8, background: 'var(--border-light)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${q.qualityScore}%`, height: '100%', borderRadius: 4, background: q.qualityScore >= 70 ? 'var(--success)' : q.qualityScore >= 40 ? 'var(--warning)' : 'var(--danger)' }} />
                      </div>
                      <span className="text-sm" style={{ fontWeight: 700 }}>{q.qualityScore}</span>
                    </div>
                  </td>
                  <td className="text-sm" style={{ maxWidth: 300 }}>{q.question.substring(0, 70)}...</td>
                  <td><span className="badge badge-neutral">{q.chapter_id}</span></td>
                  <td className="text-sm">{q.answerCount > 0 ? `${q.errorRate}%` : '—'}</td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {q.issues.map((issue, i) => (
                        <span key={i} className="badge badge-warning" style={{ fontSize: 10 }}>{issue}</span>
                      ))}
                      {q.issues.length === 0 && <span className="badge badge-success" style={{ fontSize: 10 }}><CheckCircle size={10} /> OK</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'duplicates' && (
        <div className="table-container">
          {duplicates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <CheckCircle size={24} style={{ marginBottom: 8 }} /><br />Keine Duplikate erkannt.
            </div>
          ) : (
            <table>
              <thead><tr><th>Aehnlichkeit</th><th>Frage 1</th><th>Frage 2</th></tr></thead>
              <tbody>
                {duplicates.map((d, i) => (
                  <tr key={i}>
                    <td><span className="badge badge-danger" style={{ fontWeight: 700 }}>{d.similarity}%</span></td>
                    <td className="text-sm"><span className="badge badge-neutral" style={{ marginRight: 4 }}>Kap. {d.q1.chapter}</span>{d.q1.question}...</td>
                    <td className="text-sm"><span className="badge badge-neutral" style={{ marginRight: 4 }}>Kap. {d.q2.chapter}</span>{d.q2.question}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
